import React, { useState } from "react";
import { transformToHtml, getCrId } from "../../../utils/CommonUtils";
import { FLIGHT_SEARCH_TYPE_OPTIONS } from "../constants";
import SearchTypeDropdown from "./SearchTypeDropdown";
import DateField from "./DateField";
import FlightNumberField from "./FlightNumberField";
import FlightAutosuggestField from "./FlightAutosuggestField";

const LOCATION_DISPLAY_KEYS = {
  FROM: "FROM_DISPLAY",
  TO: "TO_DISPLAY",
  AIRPORT: "AIRPORT_DISPLAY",
};

const SEARCH_TYPE_OPTIONS = [
  {
    type: FLIGHT_SEARCH_TYPE_OPTIONS.BY_FLIGHT,
    text: "By Flight Number",
    fields: {
      AIRLINE: { label: "Flight Number", placeHolder: "e.g. 6E 123" },
      DATE: { label: "Date", placeHolder: "Select date" },
    },
    fieldsOrder: ["AIRLINE", "DATE"],
  },
  {
    type: FLIGHT_SEARCH_TYPE_OPTIONS.BY_ROUTE,
    text: "By Route",
    fields: {
      FROM: { label: "From", placeHolder: "From" },
      TO: { label: "To", placeHolder: "To" },
      DATE: { label: "Date", placeHolder: "Select date" },
    },
    fieldsOrder: ["FROM", "TO", "DATE"],
  },
  {
    type: FLIGHT_SEARCH_TYPE_OPTIONS.BY_AIRPORT,
    text: "By Airport",
    fields: {
      AIRPORT: { label: "Airport", placeHolder: "Airport" },
      DATE: { label: "Date", placeHolder: "Select date" },
    },
    fieldsOrder: ["AIRPORT", "DATE"],
  },
];

const SEARCH_FIELDS = {
  FROM: { label: "From", placeHolder: "From" },
  TO: { label: "To", placeHolder: "To" },
  AIRPORT: { label: "Airport", placeHolder: "Airport" },
  AIRLINE: { label: "Flight Number", placeHolder: "e.g. 6E 123" },
  DATE: { label: "Date", placeHolder: "Select date" },
};
const DEFAULT_CTA = { text: "Check Status" };
const DEFAULT_META = { maxSelectablePastDays: 7, maxSelectableFutureDays: 30 };

export default function FlightStatusHeader({
  meta = DEFAULT_META,
  cta = DEFAULT_CTA,
  defaultSearchOption = FLIGHT_SEARCH_TYPE_OPTIONS.BY_FLIGHT,
  onSearch,
  formData: controlledFormData,
  setFormData: controlledSetFormData,
}) {
  const resolvedDefaultSearch =
    defaultSearchOption || FLIGHT_SEARCH_TYPE_OPTIONS.BY_FLIGHT;

  const [internalFormData, setInternalFormData] = useState({
    FROM: "",
    TO: "",
    AIRPORT: "",
    FROM_DISPLAY: "",
    TO_DISPLAY: "",
    AIRPORT_DISPLAY: "",
    AIRLINE: "",
    DATE: new Date().toISOString().slice(0, 10),
    selectedOption: resolvedDefaultSearch,
  });

  const formData = controlledFormData ?? internalFormData;
  const setFormData = controlledSetFormData ?? setInternalFormData;

  const selectedOption =
    formData?.selectedOption || resolvedDefaultSearch;
  const [showSearchTypeDropdown, setShowSearchTypeDropdown] = useState(false);
  const [showDatepicker, setShowDatepicker] = useState(false);
  const [openAutosuggestField, setOpenAutosuggestField] = useState(null);

  const handleFlightNumberChange = (e) => {
    setFormData({ ...formData, AIRLINE: e.target.value.toUpperCase() });
  };

  const handleDatepickerOpen = () => {
    setShowSearchTypeDropdown(false);
    setOpenAutosuggestField(null);
    setShowDatepicker((prev) => !prev);
  };

  const handleDateChange = (date, modifiers) => {
    if (modifiers?.disabled) return;
    if (date) {
      setFormData({ ...formData, DATE: date.toISOString().slice(0, 10) });
    }
    setShowDatepicker(false);
  };

  const handleSearchTypeSelect = (type) => {
    if (!type) return;
    setFormData({ ...formData, selectedOption: type });
    setShowSearchTypeDropdown(false);
    setShowDatepicker(false);
    setOpenAutosuggestField(null);
  };

  const handleAutosuggestSelect = (fieldKey, selectedLocation) => {
    const displayKey = LOCATION_DISPLAY_KEYS[fieldKey];
    setFormData({
      ...formData,
      [fieldKey]: selectedLocation.code,
      [displayKey]: selectedLocation.displayValue,
    });
  };

  const getCurrentFieldsOrder = () => {
    const option = SEARCH_TYPE_OPTIONS.find((item) => item.type === selectedOption);
    return option?.fieldsOrder || ["AIRLINE", "DATE"];
  };

  const isOutsideAllowedRange = (day) => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysAgo = new Date(today);
      daysAgo.setDate(today.getDate() - (meta?.maxSelectablePastDays ?? 7));
      const daysFromNow = new Date(today);
      daysFromNow.setDate(today.getDate() + (meta?.maxSelectableFutureDays ?? 30));
      return day < daysAgo || day > daysFromNow;
    } catch {
      return false;
    }
  };

  const enableSearch = () => {
    if (!formData.DATE) return false;
    if (selectedOption === FLIGHT_SEARCH_TYPE_OPTIONS.BY_ROUTE) {
      return !!(formData.FROM?.trim() && formData.TO?.trim());
    }
    if (selectedOption === FLIGHT_SEARCH_TYPE_OPTIONS.BY_AIRPORT) {
      return !!formData.AIRPORT?.trim();
    }
    return !!formData.AIRLINE?.trim();
  };

  const redirectToFlightStatus = () => {
    const fromParam = (formData.FROM || "").trim();
    const toParam = (formData.TO || "").trim();
    const airportParam = (formData.AIRPORT || "").trim();
    const airlineParam = (formData.AIRLINE || "").replace(/\s+/g, "");
    const dateParam = formData.DATE || new Date().toISOString().slice(0, 10);
    const params = new URLSearchParams({
      from: fromParam,
      to: toParam,
      airport: airportParam,
      airline: airlineParam,
      date: dateParam,
      selectedOption,
      crId: getCrId(),
    });
    const baseUrl =
      selectedOption === FLIGHT_SEARCH_TYPE_OPTIONS.BY_AIRPORT
        ? "https://www.makemytrip.com/flight/flight-status"
        : "https://www.makemytrip.com/flight/flight-status/detail";
    const detailUrl = `${baseUrl}?${params.toString()}`;
    if (typeof window !== "undefined") {
      window.location.href = detailUrl;
    }
  };

  const handleSearch = () => {
    if (onSearch) onSearch(selectedOption);
    else redirectToFlightStatus();
  };

  const renderField = (fieldKey) => {
    const fieldConfig = SEARCH_FIELDS[fieldKey];
    if (fieldKey === "DATE") {
      return (
        <DateField
          key="DATE"
          value={formData.DATE || ""}
          placeholder={fieldConfig.placeHolder}
          fieldConfig={fieldConfig}
          isOpen={showDatepicker}
          onOpen={handleDatepickerOpen}
          onClose={() => setShowDatepicker(false)}
          onChange={handleDateChange}
          isOutsideAllowedRange={isOutsideAllowedRange}
        />
      );
    }

    if (fieldKey === "AIRLINE") {
      return (
        <FlightNumberField
          key="AIRLINE"
          value={formData.AIRLINE || ""}
          fieldConfig={fieldConfig}
          onChange={handleFlightNumberChange}
        />
      );
    }

    if (fieldKey === "FROM" || fieldKey === "TO" || fieldKey === "AIRPORT") {
      const displayKey = LOCATION_DISPLAY_KEYS[fieldKey];
      return (
        <FlightAutosuggestField
          key={fieldKey}
          field={fieldKey}
          fieldConfig={fieldConfig}
          codeValue={formData[fieldKey] || ""}
          displayValue={formData[displayKey] || ""}
          displayType={fieldKey === "AIRPORT" ? "airport" : "city"}
          isOpen={openAutosuggestField === fieldKey}
          onOpen={() => {
            setOpenAutosuggestField(fieldKey);
            setShowDatepicker(false);
            setShowSearchTypeDropdown(false);
          }}
          onClose={() => setOpenAutosuggestField(null)}
          onSelect={(selectedLocation) => handleAutosuggestSelect(fieldKey, selectedLocation)}
        />
      );
    }

    return null;
  };

  return (
    <div className="fs-header">
      <div className="fs-header__row">
        <SearchTypeDropdown
          options={SEARCH_TYPE_OPTIONS}
          selectedOption={selectedOption}
          isOpen={showSearchTypeDropdown}
          onToggle={() => {
            setShowSearchTypeDropdown((prev) => !prev);
            setShowDatepicker(false);
            setOpenAutosuggestField(null);
          }}
          onSelect={handleSearchTypeSelect}
        />
        <div className="fs-header__fields">
          {getCurrentFieldsOrder().map((field) => renderField(field))}
        </div>
        {cta && (
          <div className="fs-header__search-btn">
            <button
              type="button"
              className="button buttonPrimary"
              onClick={handleSearch}
              disabled={!enableSearch()}
            >
              {transformToHtml(cta.text)}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
